import { Injectable } from '@angular/core';
import { mapGoogleAddress } from '@app/mappers/google-address.mapper';
import { GoogleMapsLoaderService } from './google-maps-loader.service';
import { AddressDetails, PlaceSuggestion } from '@app/interfaces/google-maps.interface';
import {
  classifyPlacesError,
  PlacesError,
} from '../shared/components/places-autocomplete/utils/helpers/places-error';

/**
 * Service responsible for interacting with the Google Maps Places API (New).
 * Handles address searching, session token management, and fetching place details.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  /** Token used to group multiple autocomplete requests into a single billing session.
   * This helps in reducing Google Maps Platform costs.
   */
  private currentSessionToken: google.maps.places.AutocompleteSessionToken | null = null;

  constructor(private loader: GoogleMapsLoaderService) {
    this.loader.injectScript();
  }

  /**
   * Invalidates the current session token.
   * Should be called after a final place selection is made to start a new billing session.
   */
  public refreshSessionToken(): void {
    this.currentSessionToken = null;
  }

  /**
   * Fetches address suggestions based on the user's input string.
   * @param query The search string (e.g., "1600 Amphitheatre Pkwy").
   * @returns A promise resolving to an array of mapped 'PlaceSuggestion' objects.
   */
  async getAutocompleteSuggestions(query: string): Promise<PlaceSuggestion[]> {
    try {
      const { AutocompleteSuggestion } = (await google.maps.importLibrary(
        'places'
      )) as google.maps.PlacesLibrary;
      const sessionToken = await this.getSessionToken();

      const request: google.maps.places.AutocompleteRequest = {
        input: query,
        includedRegionCodes: ['us'],
        sessionToken: sessionToken,
      };

      // Execute the request to Google's Autocomplete (New) service
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      return (
        suggestions
          // Filter out null predictions to ensure data integrity
          .filter(
            (suggestionItem): suggestionItem is google.maps.places.AutocompleteSuggestion =>
              suggestionItem.placePrediction !== null
          )
          .map((suggestionItem) => {
            const prediction = suggestionItem.placePrediction!;
            return {
              placeId: prediction.placeId,
              mainText: prediction.mainText?.text || '',
              secondaryText: prediction.secondaryText?.text || '',
            };
          })
      );
    } catch (err) {
      const type = classifyPlacesError(err);
      throw new PlacesError(type, err);
    }
  }

  /**
   * Retrieves detailed information (address components and geometry) for a specific place ID.
   * @param placeId The unique identifier for a place.
   * @returns A promise resolving to a processed 'AddressDetails' object.
   */
  async getPlaceDetails(placeId: string): Promise<AddressDetails> {
    try {
      const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
      const place = new Place({ id: placeId });

      await place.fetchFields({
        fields: ['addressComponents', 'location'],
      });

      // Clear session token once details are fetched to close the billing cycle
      this.refreshSessionToken();

      return mapGoogleAddress(place);
    } catch (err) {
      const type = classifyPlacesError(err);
      throw new PlacesError(type, err);
    }
  }

  /** Uses dynamic library loading for 'places' module.
   * @returns A promise resolving to the active AutocompleteSessionToken.
   */
  private async getSessionToken(): Promise<google.maps.places.AutocompleteSessionToken> {
    if (!this.currentSessionToken) {
      const { AutocompleteSessionToken } = (await google.maps.importLibrary(
        'places'
      )) as google.maps.PlacesLibrary;
      this.currentSessionToken = new AutocompleteSessionToken();
    }
    return this.currentSessionToken;
  }
}
